import React, { useState } from 'react';
import {
  Box,
  Typography,
  Button,
  Grid,
  Paper,
  Stack,
  useTheme,
} from '@mui/material';
import {
  CheckCircle,
  WorkspacePremium,
  Bolt,
} from '@mui/icons-material';
import { redirectToCheckout, STRIPE_PRICES } from '../stripe';

const tiers = [
  {
    id: 'free',
    name: 'Free',
    price: '$0',
    description: 'Perfect for casual writing and exploration.',
    features: [
      '500 words per scan',
      'Standard Rewriting mode',
      'Basic AI Detection',
      '10 Image generations / day',
      'Standard processing speed'
    ],
    buttonText: 'Select Free Plan',
    highlight: false,
    priceId: null,
  },
  {
    id: 'pro',
    name: 'Pro',
    price: '$10',
    period: '/mo',
    description: 'For students and researchers who need more power.',
    features: [
      'Unlimited words per scan',
      'All Rewriting & Humanizing modes',
      'Advanced Multi-layer AI Detection',
      'Unlimited Image generations',
      'Priority Turbo processing',
      'Early access to Smart Flow'
    ],
    buttonText: 'Upgrade to Pro',
    highlight: true,
    priceId: STRIPE_PRICES.PRO_MONTHLY,
  },
  {
    id: 'team',
    name: 'Team',
    price: '$49',
    period: '/mo',
    description: 'Shared tools for research groups and labs.',
    features: [
      'Everything in Pro',
      'Up to 5 team members',
      'Shared project dashboard',
      'API Access for integration',
      'Dedicated support',
      'Custom academic styles'
    ],
    buttonText: 'Upgrade to Team',
    highlight: false,
    priceId: STRIPE_PRICES.TEAM_MONTHLY,
  }
];

export default function Premium(): JSX.Element {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const [selectedPlan, setSelectedPlan] = useState<string>('pro'); // Default selection

  const handleSelectPlan = (planId: string) => {
    setSelectedPlan(planId);
  };

  const handleUpgrade = async (priceId: string | null, tierName: string) => {
    if (!priceId) {
      return;
    }
    await redirectToCheckout(priceId);
  };

  return (
    <Box sx={{ maxWidth: 1200, mx: 'auto', pt: 8, pb: 12 }}>
      <Box sx={{ textAlign: 'center', mb: 10 }}>
        <Typography
          variant="overline"
          sx={{
            color: 'primary.main',
            fontWeight: 800,
            letterSpacing: '0.2em',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 1,
            mb: 2
          }}
        >
          <WorkspacePremium fontSize="small" /> PRICING PLANS
        </Typography>
        <Typography variant="h2" sx={{ fontWeight: 800, color: 'text.primary', mb: 2, trackingTight: '-0.04em' }}>
          Upgrade your <span style={{ color: isDark ? '#4b5563' : theme.palette.primary.main }}>research</span>
        </Typography>
        <Typography variant="h6" sx={{ color: isDark ? 'text.secondary' : '#374151', fontWeight: 500, maxWidth: 600, mx: 'auto', lineHeight: 1.6 }}>
          Choose the plan that fits your writing and research needs. Unlock the full potential of CoWrite AI.
        </Typography>
      </Box>

      <Grid container spacing={4} alignItems="stretch">
        {tiers.map((tier) => {
          const isSelected = selectedPlan === tier.id;

          return (
            <Grid item xs={12} md={4} key={tier.name}>
              <Paper
                elevation={0}
                onClick={() => handleSelectPlan(tier.id)}
                sx={{
                  p: 5,
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  borderRadius: '32px',
                  bgcolor: isSelected
                    ? (isDark ? 'rgba(59, 130, 246, 0.08)' : 'rgba(59, 130, 246, 0.04)')
                    : 'background.paper',
                  border: '2px solid', // Thicker border for selection
                  borderColor: isSelected
                    ? 'primary.main'
                    : (isDark ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.08)'),
                  position: 'relative',
                  overflow: 'hidden',
                  transition: 'all 0.3s ease',
                  cursor: 'pointer',
                  boxShadow: isSelected
                    ? (isDark ? '0 0 0 1px #3b82f6' : '0 10px 40px -10px rgba(59, 130, 246, 0.15)')
                    : (isDark ? 'none' : '0 10px 40px -10px rgba(0,0,0,0.05)'),
                  '&:hover': {
                    transform: 'translateY(-8px)',
                    boxShadow: isDark ? 'none' : '0 20px 40px -10px rgba(0,0,0,0.1)',
                    borderColor: isSelected ? 'primary.main' : (isDark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.15)')
                  }
                }}
              >
                {tier.highlight && (
                  <Box
                    sx={{
                      position: 'absolute',
                      top: 25,
                      right: -32,
                      bgcolor: 'primary.main',
                      color: 'white',
                      py: 0.5,
                      px: 5,
                      transform: 'rotate(45deg)',
                      fontSize: '11px',
                      fontWeight: 800,
                      letterSpacing: '0.1em',
                      boxShadow: '0 4px 12px rgba(37, 99, 235, 0.4)'
                    }}
                  >
                    BEST VALUE
                  </Box>
                )}

                <Typography variant="h5" sx={{ color: 'text.primary', fontWeight: 800, mb: 1 }}>
                  {tier.name}
                </Typography>
                <Typography variant="body2" sx={{ color: 'text.secondary', mb: 4, minHeight: 40 }}>
                  {tier.description}
                </Typography>

                <Box sx={{ display: 'flex', alignItems: 'baseline', mb: 5 }}>
                  <Typography variant="h3" sx={{ color: 'text.primary', fontWeight: 800 }}>
                    {tier.price}
                  </Typography>
                  {tier.period && (
                    <Typography variant="h6" sx={{ color: isDark ? '#9ca3af' : '#374151', ml: 0.5, alignSelf: 'flex-end', mb: 0.5 }}>
                      {tier.period}
                    </Typography>
                  )}
                </Box>

                <Stack spacing={2} sx={{ mb: 6, flexGrow: 1 }}>
                  {tier.features.map((feature) => (
                    <Box key={feature} sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                      <CheckCircle sx={{ color: isSelected ? 'primary.main' : (isDark ? '#10b981' : '#059669'), fontSize: 20 }} />
                      <Typography variant="body2" sx={{ color: 'text.secondary', fontWeight: 500 }}>
                        {feature}
                      </Typography>
                    </Box>
                  ))}
                </Stack>

                <Button
                  fullWidth
                  variant={isSelected ? 'contained' : 'outlined'}
                  onClick={(e) => {
                    e.stopPropagation(); // Prevent card selection when clicking button
                    handleUpgrade(tier.priceId, tier.name);
                  }}
                  sx={{
                    py: 1.5,
                    borderRadius: '16px',
                    textTransform: 'none',
                    fontWeight: 700,
                    boxShadow: isSelected ? '0 4px 12px rgba(37, 99, 235, 0.3)' : 'none',
                    ...(isSelected ? {
                      bgcolor: 'primary.main',
                      color: 'white',
                      '&:hover': {
                        bgcolor: 'primary.dark',
                      }
                    } : {
                      color: 'text.primary',
                      borderColor: isDark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)',
                      '&:hover': {
                        bgcolor: isDark ? 'rgba(255,255,255,0.03)' : 'rgba(0,0,0,0.03)',
                        borderColor: 'text.primary'
                      },
                      '&:disabled': {
                        borderColor: isDark ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.05)',
                      }
                    })
                  }}
                >
                  {tier.buttonText}
                </Button>
              </Paper>
            </Grid>
          );
        })}
      </Grid>

      {/* Trust Badges */}
      <Box sx={{ mt: 16, textAlign: 'center' }}>
        <Typography variant="caption" sx={{ color: 'text.secondary', mb: 4, letterSpacing: '0.1em', fontWeight: 600, display: 'block' }}>
          POWERED BY INDUSTRY LEADERS
        </Typography>
        <Stack
          direction="row"
          spacing={6}
          justifyContent="center"
          alignItems="center"
          sx={{
            opacity: isDark ? 0.4 : 0.6,
            filter: 'grayscale(1)',
            transition: 'opacity 0.3s',
            '&:hover': { opacity: 1, filter: 'grayscale(0)' }
          }}
        >
          <Box
            component="a"
            href="https://google.com"
            target="_blank"
            rel="noopener noreferrer"
            sx={{ display: 'block', transition: 'transform 0.2s', '&:hover': { transform: 'scale(1.1)' } }}
          >
            <Box component="img" src="https://upload.wikimedia.org/wikipedia/commons/2/2f/Google_2015_logo.svg" sx={{ height: 26, display: 'block' }} />
          </Box>

          <Box
            component="a"
            href="https://www.deepl.com"
            target="_blank"
            rel="noopener noreferrer"
            sx={{ display: 'block', transition: 'transform 0.2s', '&:hover': { transform: 'scale(1.1)' } }}
          >
            <Box component="img" src="https://upload.wikimedia.org/wikipedia/commons/b/bb/DeepL_Logo.svg" sx={{ height: 26, display: 'block' }} />
          </Box>

          <Stack
            component="a"
            href="https://deepmind.google/technologies/gemini/"
            target="_blank"
            rel="noopener noreferrer"
            direction="row"
            spacing={1}
            alignItems="center"
            sx={{ color: isDark ? 'white' : '#5f6368', textDecoration: 'none', transition: 'transform 0.2s', '&:hover': { transform: 'scale(1.1)' } }}
          >
            <Bolt /> <Typography variant="h6" sx={{ fontWeight: 800 }}>GEMINI</Typography>
          </Stack>
        </Stack>
      </Box>
    </Box>
  );
}
